/**
 * Leaving with unsaved work (manager redesign, 2026-10-09; one question since issue #72): leaving
 * the page by link, menu or Back, and an exit inside a page (askBeforeLeaving in context.ts:
 * another week, another shift, closing a panel), get the same question with the same three answers.
 * An editor declares its work with useDirty (context.ts) and, when its save is neutral, hands over
 * that save here, so "Lagre og fortsett" can finish it. A decision (approve, reject, delete,
 * publish) never registers a save: the generic button must not choose for the manager.
 * Two answers, never three (owner, 2026-10-20: three buttons confused a prospect): save or discard
 * when the work can be saved, keep editing or discard when it cannot; Escape or a click outside
 * keeps editing. An editor that answers in its own panel (useInlineLeave) gets no dialog at all.
 */
import { T } from '@/i18n';
import { ctx, forgetDirty, setLeaving, useContextStore } from '@/shell/context';
import { Button } from '@/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';

const saves = new Map<string, () => Promise<boolean>>();
/** editors that show the leave question in their own panel (the shift editor, owner 2026-10-20) */
const inline = new Set<string>();

/** This editor answers the leave question itself, inside its panel, instead of the shell's dialog. */
export function useInlineLeave(id: string) {
  useEffect(() => {
    inline.add(id);
    return () => {
      inline.delete(id);
    };
  }, [id]);
}
/** true when every piece of unsaved work belongs to an editor that answers in its own panel */
export function answeredInline(): boolean {
  const ids = Object.keys(ctx().dirty);
  return ids.length > 0 && ids.every((k) => inline.has(k));
}

/** An editor's neutral save: resolves true when it saved, false when it did not (validation shows beside the field). */
export function useNeutralSave(id: string, save: (() => Promise<boolean>) | null) {
  const ref = useRef(save);
  ref.current = save;
  useEffect(() => {
    if (!save) return;
    saves.set(id, () => (ref.current ? ref.current() : Promise.resolve(false)));
    return () => {
      saves.delete(id);
    };
  }, [id, save]);
}

export type Choice = 'save' | 'discard' | 'stay';

/** true when every open editor handed over a neutral save, so one button may finish them all */
export function canSaveAll(): boolean {
  const ids = Object.keys(ctx().dirty);
  return ids.length > 0 && ids.every((k) => saves.has(k));
}
/** every open editor's save, in parallel; true only when each one saved */
export async function saveAll(): Promise<boolean> {
  const ids = Object.keys(ctx().dirty);
  const results = await Promise.all(ids.map((k) => saves.get(k)?.() ?? Promise.resolve(false)));
  return results.every(Boolean);
}

/**
 * The question itself: a Radix dialog of its own, so it stacks above an open drawer (whose backdrop
 * and focus trap would otherwise swallow it) and takes focus; closing it any way means "keep editing".
 */
function Question({
  to,
  busy,
  onAnswer,
  keepFocus,
}: { to: string | null; busy: boolean; onAnswer: (c: Choice) => void; keepFocus: boolean }) {
  const dirty = useContextStore((s) => s.dirty);
  const ids = Object.keys(dirty);
  const what = ids.map((k) => dirty[k]).join(', ');
  const savable = ids.length > 0 && ids.every((k) => saves.has(k));
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onAnswer('stay')}>
      <Dialog.Portal>
        <Dialog.Overlay className="scrim leavescrim" />
        <Dialog.Content
          className="confirmbox"
          role="alertdialog"
          aria-describedby="leave-body"
          data-testid="leave"
          // an exit inside the page leaves focus where the page puts it (a refused save on its field)
          onCloseAutoFocus={(e) => keepFocus && e.preventDefault()}
        >
          <Dialog.Title asChild>
            <h3>{T('Du har ulagrede endringer')}</h3>
          </Dialog.Title>
          <p id="leave-body">
            {T('{what} er ikke lagret. Hva vil du gjøre før du går videre?', { what })}
            {to ? ` ${T('Du var på vei til: {to}.', { to })}` : ''}
          </p>
          <div className="df">
            {savable ? (
              <Button tone="pri" disabled={busy} onClick={() => onAnswer('save')} autoFocus>
                {T('Lagre og fortsett')}
              </Button>
            ) : (
              <Button onClick={() => onAnswer('stay')} autoFocus>
                {T('Fortsett å redigere')}
              </Button>
            )}
            <Button ghost disabled={busy} onClick={() => onAnswer('discard')}>
              {T('Forkast og fortsett')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Leaving the page: React Router's blocker, held while the person answers. */
function RouteQuestion() {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      Object.keys(ctx().dirty).length > 0 && currentLocation.pathname !== nextLocation.pathname,
  );
  const [busy, setBusy] = useState(false);
  const blocked = blocker.state === 'blocked';
  // an editor answering in its own panel gets the page exit as an exit inside the page
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per blocked navigation
  useEffect(() => {
    if (!blocked || !answeredInline()) return;
    setLeaving({ label: '', go: () => blocker.proceed?.(), stay: () => blocker.reset?.() });
  }, [blocked]);
  if (!blocked || answeredInline()) return null;
  const answer = async (c: Choice) => {
    if (c === 'stay') return blocker.reset?.();
    if (c === 'discard') return blocker.proceed?.();
    setBusy(true);
    try {
      // a refused save keeps the person on the page, with the field's own message showing
      if (await saveAll()) blocker.proceed?.();
      else blocker.reset?.();
    } finally {
      setBusy(false);
    }
  };
  return <Question to={null} busy={busy} onAnswer={answer} keepFocus={false} />;
}

/** An exit inside the page (askBeforeLeaving): the same answers, then the page's own go or stay. */
function IntentQuestion() {
  const leaving = useContextStore((s) => s.leaving);
  if (!leaving || answeredInline()) return null;
  const answer = async (c: Choice) => {
    setLeaving(null);
    if (c === 'stay') return leaving.stay?.();
    if (c === 'discard') {
      forgetDirty();
      leaving.discard?.();
      return leaving.go();
    }
    // the question closes before the save runs, so a refused save can focus its own field
    await new Promise((r) => setTimeout(r, 0));
    if (await saveAll()) leaving.go();
    else leaving.stay?.();
  };
  return <Question to={leaving.label} busy={false} onAnswer={answer} keepFocus />;
}

/**
 * The answers for an editor that asks in its own panel: the same three outcomes as the dialog.
 * `save` resolves true when it saved; the page's go runs only then.
 */
export async function answerLeaving(c: Choice, save?: () => Promise<boolean>) {
  const leaving = ctx().leaving;
  if (!leaving) return;
  setLeaving(null);
  if (c === 'stay') return leaving.stay?.();
  if (c === 'discard') {
    forgetDirty();
    leaving.discard?.();
    return leaving.go();
  }
  if (save && (await save())) leaving.go();
  else leaving.stay?.();
}

/** Mounted once by the shell, on every page; the route half needs the app's data router. */
export function LeaveGuard() {
  const dirty = useContextStore((s) => s.dirty);
  const any = Object.keys(dirty).length > 0;
  const routed = useContext(UNSAFE_DataRouterContext) != null;
  // closing the tab or reloading with work open: the browser's own question
  useEffect(() => {
    if (!any) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [any]);
  return (
    <>
      {routed && <RouteQuestion />}
      <IntentQuestion />
    </>
  );
}
